import React, { useMemo, useRef, useState, useCallback } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import { useLanguage } from "../../../contexts/LanguageContext";

const DealerWarehouseTable = ({
  rows = [],
  loading = false,
  filters,
  totalCount = 0,
  pagination = { PageNumber: 1, PageSize: 10 },
  onPageChange = () => {},
  handleEdit = () => {},
  handleDelete = () => {}
}) => {
  const { th } = useLanguage();
  const dataTableRef = useRef(null);
  const columnsRef = useRef(null);

  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(0); 

  const getColumnsCallback = useCallback((el) => {
    if (el && el.getColumns && !columnsRef.current) columnsRef.current = el.getColumns();
    dataTableRef.current = el;
  }, []);

  const confirmDelete = (row) => {
    confirmDialog({
      message: th("dealerWarehouse.confirm.deleteMessage") || "Kayıt silinsin mi?",
      header: th("dealerWarehouse.confirm.deleteHeader") || "Silme Onayı",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: th("common.yes") || "Evet",
      rejectLabel: th("common.no") || "Hayır",
      accept: () => handleDelete(row),
      rejectClassName: "p-button-outlined"
    });
  };

  const actionBodyTemplate = (row) => (
    <div className="flex items-center gap-3">
      <i
        onClick={() => handleEdit(row)}
        className="pi pi-pencil cursor-pointer text-blue-500 hover:text-blue-700"
        title={th("dealerWarehouse.edit") || "Düzenle"}
      />
      <i
        onClick={() => confirmDelete(row)}
        className="pi pi-trash cursor-pointer text-red-500 hover:text-red-700"
        title={th("dealerWarehouse.delete") || "Sil"}
      />
    </div>
  );

  const statusTemplate = (row) => (
    <Tag
      value={row.isActive === 1 ? (th("common.active") || "Aktif") : (th("common.inactive") || "Pasif")}
      severity={row.isActive === 1 ? "success" : "danger"}
    />
  );

  const styleTemplate = (row) =>
    row.warehouseStyle === 1 ? (th("dealerWarehouse.fixed") || "Sabit")
      : row.warehouseStyle === 2 ? (th("dealerWarehouse.mobile") || "Gezici")
      : "-";


  const normalized = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);

  const sorted = useMemo(() => {
    if (!sortField || sortOrder === 0) return normalized;

    const copy = [...normalized];

    return copy.sort((a, b) => {
      let av = a?.[sortField];
      let bv = b?.[sortField];

      if (av == null && bv == null) return 0;
      if (av == null) return sortOrder;    
      if (bv == null) return -sortOrder;

      if (typeof av === "string") av = av.toLowerCase();
      if (typeof bv === "string") bv = bv.toLowerCase();

      if (av < bv) return -sortOrder;
      if (av > bv) return sortOrder;
      return 0;
    });
  }, [normalized, sortField, sortOrder]);

  const handleSort = (e) => {
    setSortField(e.sortField);
    setSortOrder(e.sortOrder);
  };

  return (
    <>
      <ConfirmDialog />
      <DataTable
        key={`wh-table-${sorted.length}-${loading}-${sortField}-${sortOrder}`}
        value={sorted}
        dataKey="id"
        lazy
        paginator
        first={(pagination?.PageNumber - 1) * (pagination?.PageSize || 10)}
        rows={pagination?.PageSize || 10}
        totalRecords={totalCount}
        onPage={onPageChange}
        onSort={handleSort}
        sortField={sortField}
        sortOrder={sortOrder}
        loading={loading}
        filters={filters}
        filterDisplay="menu"
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
        tableStyle={{ minWidth: "50rem" }}
        showGridlines
        stripedRows
        sortMode="single"
        ref={getColumnsCallback}
        emptyMessage={th("dealerWarehouse.empty") || "Kayıt bulunamadı"}
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        currentPageReportTemplate={th("table.paginatorTemplate")}
      >
        <Column body={actionBodyTemplate} exportable={false} style={{ width: "5%", minWidth: "53px" }} />

        <Column field="warehouseName" header={th("dealerWarehouse.warehouseName") || "Ambar Adı"} sortable />
        <Column field="relatedWarehouseName" header={th("dealerWarehouse.parent") || "Bağlı Olduğu Ambar"} sortable />
        <Column field="warehouseTypeName" header={th("dealerWarehouse.type") || "Stok Yeri"} sortable />

        <Column field="warehouseStyle" header={th("dealerWarehouse.style") || "Türü"} body={styleTemplate} sortable />
        <Column field="isActive" header={th("dealerWarehouse.status") || "Durum"} body={statusTemplate} sortable />
      </DataTable>
    </>
  );
};

export default DealerWarehouseTable;
